/**
 * reportEngine.js — Synthetic workforce-report data generator (SALES DEMO ONLY)
 *
 * Zero server load, zero AI, zero DB. Pure deterministic JS.
 * A salesperson types a prospect's company + rough hierarchy; this fabricates
 * realistic-looking, INTERNALLY-CONSISTENT metrics so the generated PDF reads
 * like a genuine TeamGrid intelligence report ("this is what you'd get").
 *
 * Determinism: everything is seeded off the company name, so the same company
 * always regenerates the identical report (stable for repeated demos).
 *
 * NOTE: All numbers here are fabricated for demonstration. The rendered report
 * carries a clear "Illustrative sample" disclaimer (see reportTemplates.js).
 */

// ─── Seeded PRNG (mulberry32) ────────────────────────────────────────────────
function hashStr(s) {
  let h = 1779033703 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return h >>> 0;
}
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─── Industry profiles — tune vocabulary so the report reads domain-authentic ──
// Each profile: display label, work-type mix (label + weight), tool set,
// automation candidates (task + %automatable + annual ₹ recoverable per ~person).
export const INDUSTRIES = {
  software: {
    label: 'Software / IT Services',
    verdictNoun: 'engineering month',
    work: [
      ['Feature development', 26], ['Code review & QA', 14], ['Bug fixing & support', 13],
      ['Architecture & design', 9], ['DevOps & deployment', 8], ['Client communication', 11],
      ['AI-assisted work', 12], ['Documentation', 7],
    ],
    tools: ['VS Code', 'GitHub', 'ChatGPT', 'Jira', 'Docker', 'Postman', 'Slack', 'Figma', 'Gemini', 'Notion'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['Automated test generation & QA triage', 70, 380000],
      ['Deployment & release pipelines', 65, 260000],
      ['Status reporting & standup summaries', 80, 180000],
      ['AI-assisted code review pre-screen', 60, 240000],
    ],
  },
  ca: {
    label: 'CA / Accounting / Audit',
    verdictNoun: 'compliance month',
    work: [
      ['GST filing & reconciliation', 22], ['Income-Tax / TDS', 20], ['Audit & assurance', 15],
      ['Bookkeeping (Tally)', 14], ['Advisory', 7], ['Client communication', 11], ['AI-assisted work', 11],
    ],
    tools: ['Tally Prime', 'Winman', 'GST Portal', 'Income-Tax Portal', 'Excel', 'ChatGPT', 'Zoho Books', 'WhatsApp', 'Gemini'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['GST reconciliation & return prep', 80, 420000],
      ['TDS challans & returns', 75, 260000],
      ['Bank reconciliation & bookkeeping', 70, 300000],
      ['Recurring compliance reminders', 85, 150000],
    ],
  },
  marketing: {
    label: 'Marketing / Research Agency',
    verdictNoun: 'campaign month',
    work: [
      ['Research & analysis', 24], ['Content & copywriting', 18], ['Campaign execution', 15],
      ['Lead generation', 12], ['Design & creative', 10], ['Client communication', 10], ['AI-assisted work', 11],
    ],
    tools: ['ChatGPT', 'HubSpot', 'Apollo', 'Canva', 'Google Ads', 'Meta Ads', 'Notion', 'Gemini', 'Figma', 'Excel'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['AI-assisted content drafting', 75, 340000],
      ['Lead enrichment & list building', 80, 280000],
      ['Campaign reporting & dashboards', 70, 200000],
      ['Research synthesis & summarisation', 65, 260000],
    ],
  },
  sales: {
    label: 'Sales / Business Development',
    verdictNoun: 'sales month',
    work: [
      ['Prospecting & outreach', 24], ['Demos & meetings', 18], ['CRM & pipeline hygiene', 14],
      ['Proposal & quoting', 12], ['Follow-ups', 12], ['Client communication', 12], ['AI-assisted work', 8],
    ],
    tools: ['HubSpot', 'Salesforce', 'Apollo', 'LinkedIn', 'ChatGPT', 'Zoom', 'Gmail', 'Excel', 'Slack'],
    aiTools: ['ChatGPT'],
    automations: [
      ['Outreach sequencing & follow-ups', 80, 360000],
      ['CRM data entry & enrichment', 75, 260000],
      ['Proposal & quote generation', 70, 220000],
      ['Call notes & pipeline summaries', 70, 190000],
    ],
  },
  operations: {
    label: 'Operations / BPO / Support',
    verdictNoun: 'operations month',
    work: [
      ['Ticket handling & support', 24], ['Data entry & processing', 18], ['Process execution', 16],
      ['Quality checks', 11], ['Reporting', 10], ['Client communication', 12], ['AI-assisted work', 9],
    ],
    tools: ['Zendesk', 'Excel', 'ChatGPT', 'Freshdesk', 'Slack', 'Google Sheets', 'CRM', 'Gemini'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['AI-assisted ticket triage & replies', 75, 320000],
      ['Data entry & validation', 80, 280000],
      ['Recurring report generation', 85, 210000],
      ['SOP & knowledge-base drafting', 60, 160000],
    ],
  },
  legal: {
    label: 'Law Firm / Legal Services',
    verdictNoun: 'legal month',
    work: [
      ['Drafting & documentation', 24], ['Case research', 18], ['Court & filing work', 13],
      ['Client consultation', 14], ['Due diligence', 9], ['Client communication', 11], ['AI-assisted work', 11],
    ],
    tools: ['Manupatra', 'SCC Online', 'MS Word', 'ChatGPT', 'Excel', 'Adobe Acrobat', 'WhatsApp', 'Gemini', 'Outlook'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['AI-assisted contract & document drafting', 70, 420000],
      ['Case-law research & summarisation', 65, 300000],
      ['Filing reminders & docket tracking', 80, 180000],
      ['Client intake & document collection', 70, 200000],
    ],
  },
  healthcare: {
    label: 'Clinic / Healthcare',
    verdictNoun: 'clinical month',
    work: [
      ['Patient consultation', 22], ['Records & EMR', 16], ['Billing & claims', 14],
      ['Appointment scheduling', 13], ['Diagnostics coordination', 10], ['Patient communication', 14], ['AI-assisted work', 11],
    ],
    tools: ['Practo', 'EMR', 'Excel', 'WhatsApp', 'ChatGPT', 'Tally', 'Google Calendar', 'Gemini'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['Appointment scheduling & reminders', 85, 300000],
      ['Insurance claims & billing prep', 75, 280000],
      ['Patient records digitisation', 70, 220000],
      ['Follow-up & recall messaging', 80, 160000],
    ],
  },
  realestate: {
    label: 'Real Estate / Brokerage',
    verdictNoun: 'sales month',
    work: [
      ['Lead follow-up', 22], ['Site visits & showings', 16], ['Listing management', 14],
      ['Negotiation & closing', 13], ['Documentation', 11], ['Client communication', 13], ['AI-assisted work', 11],
    ],
    tools: ['99acres', 'MagicBricks', 'CRM', 'WhatsApp', 'Excel', 'ChatGPT', 'Canva', 'Google Maps', 'Gemini'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['Lead capture & auto follow-up', 80, 340000],
      ['Listing content & posting', 75, 220000],
      ['Site-visit scheduling', 80, 180000],
      ['Deal documentation & templates', 70, 200000],
    ],
  },
  ecommerce: {
    label: 'E-commerce / D2C',
    verdictNoun: 'commerce month',
    work: [
      ['Catalogue & listings', 20], ['Order & fulfilment ops', 17], ['Performance marketing', 15],
      ['Customer support', 13], ['Analytics & reporting', 10], ['Client communication', 10], ['AI-assisted work', 15],
    ],
    tools: ['Shopify', 'Meta Ads', 'Google Ads', 'Excel', 'ChatGPT', 'Canva', 'Amazon Seller', 'Notion', 'Gemini'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['AI product listing & description writing', 80, 320000],
      ['Ad creative & campaign reporting', 70, 260000],
      ['Order tracking & support replies', 75, 240000],
      ['Inventory & reorder alerts', 80, 200000],
    ],
  },
  manufacturing: {
    label: 'Manufacturing / SME',
    verdictNoun: 'production month',
    work: [
      ['Production planning', 20], ['Procurement & vendors', 16], ['Quality control', 14],
      ['Inventory management', 14], ['Dispatch & logistics', 11], ['Client communication', 12], ['AI-assisted work', 13],
    ],
    tools: ['Tally', 'Excel', 'SAP', 'ChatGPT', 'WhatsApp', 'ERP', 'Google Sheets', 'Gemini'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['Purchase-order & vendor follow-up', 75, 300000],
      ['Inventory tracking & reorder', 80, 280000],
      ['Production & dispatch reporting', 80, 220000],
      ['Quality-log digitisation', 65, 170000],
    ],
  },
  edtech: {
    label: 'EdTech / Coaching',
    verdictNoun: 'academic month',
    work: [
      ['Content & curriculum', 20], ['Live teaching & sessions', 18], ['Student support', 14],
      ['Admissions & counselling', 14], ['Assessment & grading', 10], ['Client communication', 11], ['AI-assisted work', 13],
    ],
    tools: ['Zoom', 'Google Classroom', 'ChatGPT', 'Canva', 'Excel', 'Notion', 'WhatsApp', 'Gemini', 'YouTube Studio'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['AI content & lesson-plan drafting', 75, 320000],
      ['Admissions follow-up & counselling', 80, 260000],
      ['Auto-grading & feedback', 70, 220000],
      ['Attendance & progress reporting', 80, 170000],
    ],
  },
  nbfc: {
    label: 'NBFC / Financial Services',
    verdictNoun: 'lending month',
    work: [
      ['Loan processing', 20], ['Credit underwriting', 16], ['Collections & recovery', 15],
      ['KYC & compliance', 14], ['Reporting', 10], ['Client communication', 12], ['AI-assisted work', 13],
    ],
    tools: ['Excel', 'LOS/LMS', 'ChatGPT', 'CRM', 'Tally', 'CIBIL', 'WhatsApp', 'Gemini'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['Loan-document verification & KYC', 75, 360000],
      ['Collections reminders & workflows', 80, 300000],
      ['Credit-report summarisation', 70, 260000],
      ['Regulatory & MIS reporting', 80, 200000],
    ],
  },
  recruitment: {
    label: 'Recruitment / Staffing',
    verdictNoun: 'hiring month',
    work: [
      ['Sourcing & screening', 24], ['Interviews & coordination', 16], ['Client & candidate comms', 15],
      ['Offer & onboarding', 11], ['Database management', 10], ['Reporting', 10], ['AI-assisted work', 14],
    ],
    tools: ['LinkedIn', 'Naukri', 'ATS', 'Excel', 'ChatGPT', 'Gmail', 'WhatsApp', 'Gemini', 'Zoom'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['AI resume screening & shortlisting', 80, 340000],
      ['Interview scheduling & coordination', 80, 240000],
      ['Candidate outreach & follow-up', 75, 260000],
      ['Pipeline & submission reporting', 80, 180000],
    ],
  },
  architecture: {
    label: 'Architecture / Interior Design',
    verdictNoun: 'design month',
    work: [
      ['Design & drafting', 24], ['3D visualisation', 15], ['Site coordination', 13],
      ['Client presentations', 13], ['BOQ & estimation', 11], ['Client communication', 11], ['AI-assisted work', 13],
    ],
    tools: ['AutoCAD', 'SketchUp', 'Revit', 'ChatGPT', 'Photoshop', 'Excel', 'Canva', 'Gemini', 'WhatsApp'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['AI-assisted concept & mood-board drafting', 65, 280000],
      ['BOQ & estimation automation', 75, 300000],
      ['Project status & site reporting', 80, 200000],
      ['Client proposal generation', 70, 210000],
    ],
  },
  logistics: {
    label: 'Logistics / Freight',
    verdictNoun: 'operations month',
    work: [
      ['Shipment coordination', 22], ['Fleet & route planning', 16], ['Documentation & customs', 14],
      ['Vendor management', 12], ['Tracking & updates', 12], ['Client communication', 12], ['AI-assisted work', 12],
    ],
    tools: ['Excel', 'TMS', 'ChatGPT', 'WhatsApp', 'Google Maps', 'Tally', 'ERP', 'Gemini'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['Shipment tracking & status updates', 85, 300000],
      ['Documentation & e-way bill prep', 75, 260000],
      ['Route & load optimisation', 70, 240000],
      ['Vendor follow-up & reconciliation', 75, 190000],
    ],
  },
  media: {
    label: 'Media / Video Production',
    verdictNoun: 'production month',
    work: [
      ['Editing & post-production', 24], ['Shooting & production', 15], ['Content planning', 13],
      ['Client revisions', 12], ['Publishing & distribution', 11], ['Client communication', 11], ['AI-assisted work', 14],
    ],
    tools: ['Premiere Pro', 'After Effects', 'ChatGPT', 'Photoshop', 'YouTube Studio', 'Canva', 'Notion', 'Gemini', 'Frame.io'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['AI scripting & content ideation', 70, 300000],
      ['Auto-captioning & rough cuts', 75, 260000],
      ['Publishing & scheduling', 80, 180000],
      ['Asset organisation & review flow', 65, 170000],
    ],
  },
  insurance: {
    label: 'Insurance / Advisory',
    verdictNoun: 'advisory month',
    work: [
      ['Policy sales & advisory', 22], ['Claims processing', 16], ['Renewals & servicing', 15],
      ['Underwriting support', 11], ['Documentation', 11], ['Client communication', 13], ['AI-assisted work', 12],
    ],
    tools: ['CRM', 'Excel', 'ChatGPT', 'WhatsApp', 'Insurer Portals', 'Gmail', 'Tally', 'Gemini'],
    aiTools: ['ChatGPT', 'Gemini'],
    automations: [
      ['Renewal reminders & follow-up', 85, 300000],
      ['Claims documentation & tracking', 75, 260000],
      ['Policy comparison & quote generation', 70, 240000],
      ['Client servicing & MIS reporting', 80, 180000],
    ],
  },
};

export const INDUSTRY_KEYS = Object.keys(INDUSTRIES);

// ─── Industry lingo — makes the narrative read authentic to each vertical ────
// unit=what a person is called · team=collective noun · deadline=the crunch moment ·
// routine=the repetitive work · seniorValue=where senior time should go ·
// crossTrain=coverage phrasing.
const DEFAULT_LINGO = {
  unit: 'person', team: 'team', deadline: 'a deadline', routine: 'routine, repeatable work',
  seniorValue: 'review and higher-value work', crossTrain: 'cross-train and document recurring workflows',
};
export const INDUSTRY_LINGO = {
  software: { unit: 'engineer', team: 'engineering team', deadline: 'a release or sprint deadline', routine: 'testing, deployment and status reporting', seniorValue: 'architecture and review', crossTrain: 'pair on critical services and document runbooks' },
  ca: { unit: 'professional', team: 'practice', deadline: 'a filing deadline', routine: 'GST, TDS and reconciliation work', seniorValue: 'review, sign-off and advisory', crossTrain: 'cross-train on client portfolios and document filing checklists' },
  marketing: { unit: 'team member', team: 'agency team', deadline: 'a campaign launch', routine: 'reporting, list-building and content production', seniorValue: 'strategy and client relationships', crossTrain: 'document campaign playbooks and cross-train on accounts' },
  sales: { unit: 'rep', team: 'sales team', deadline: 'quarter-end', routine: 'CRM hygiene, follow-ups and quoting', seniorValue: 'closing and key accounts', crossTrain: 'share account context and document the sales playbook' },
  operations: { unit: 'executive', team: 'operations team', deadline: 'an SLA or month-end', routine: 'ticket handling, data entry and reporting', seniorValue: 'escalations and process improvement', crossTrain: 'cross-train across queues and document SOPs' },
  legal: { unit: 'associate', team: 'firm', deadline: 'a hearing or filing date', routine: 'drafting, research and documentation', seniorValue: 'strategy, argument and client counsel', crossTrain: 'cross-staff matters and maintain matter notes' },
  healthcare: { unit: 'clinician', team: 'clinic', deadline: 'a busy OPD day', routine: 'records, billing and scheduling', seniorValue: 'diagnosis and patient care', crossTrain: 'cross-train front-desk and clinical support' },
  realestate: { unit: 'agent', team: 'brokerage', deadline: 'a closing', routine: 'lead follow-up, listings and paperwork', seniorValue: 'negotiation and closing', crossTrain: 'share the lead pipeline and document deal steps' },
  ecommerce: { unit: 'team member', team: 'D2C team', deadline: 'a sale or launch', routine: 'listings, order ops and ad reporting', seniorValue: 'growth strategy and merchandising', crossTrain: 'document listing and fulfilment SOPs' },
  manufacturing: { unit: 'staff member', team: 'plant', deadline: 'a dispatch or order deadline', routine: 'procurement follow-up, inventory and dispatch reporting', seniorValue: 'planning and quality oversight', crossTrain: 'cross-train operators and document line procedures' },
  edtech: { unit: 'educator', team: 'academy', deadline: 'an admissions or exam cycle', routine: 'content prep, grading and admissions follow-up', seniorValue: 'teaching and curriculum', crossTrain: 'share lesson plans and cross-train counsellors' },
  nbfc: { unit: 'officer', team: 'lending team', deadline: 'a disbursal or reporting deadline', routine: 'KYC, verification and collections follow-up', seniorValue: 'underwriting judgement and risk', crossTrain: 'cross-train on credit files and document the process' },
  recruitment: { unit: 'recruiter', team: 'delivery team', deadline: 'a client submission deadline', routine: 'sourcing, screening and scheduling', seniorValue: 'client relationships and closing candidates', crossTrain: 'share the candidate pipeline and account notes' },
  architecture: { unit: 'designer', team: 'studio', deadline: 'a client presentation or site milestone', routine: 'drafting, visualisation and estimation', seniorValue: 'design direction and client presentation', crossTrain: 'maintain project files and cross-staff drawings' },
  logistics: { unit: 'executive', team: 'operations team', deadline: 'a shipment cut-off', routine: 'tracking, documentation and vendor follow-up', seniorValue: 'planning and client servicing', crossTrain: 'cross-train on lanes and document handling steps' },
  media: { unit: 'creator', team: 'studio', deadline: 'a delivery or publish date', routine: 'editing, captioning and publishing', seniorValue: 'creative direction and client review', crossTrain: 'organise assets and document the edit workflow' },
  insurance: { unit: 'advisor', team: 'advisory team', deadline: 'a renewal or claim deadline', routine: 'renewals, claims paperwork and servicing', seniorValue: 'advisory and key relationships', crossTrain: 'share the client book and document servicing steps' },
};
export const lingoFor = (key) => ({ ...DEFAULT_LINGO, ...(INDUSTRY_LINGO[key] || {}) });

// ─── Company-name → industry auto-detection ──────────────────────────────────
// Ordered keyword rules mapping a typed/fetched company name to the most likely
// vertical. Pure string matching — no AI, instant. First match wins, so more
// specific patterns are listed first. Rep can always override the suggestion.
const INDUSTRY_NAME_RULES = [
  ['ca', /(&\s*associates|and associates|&\s*co\.?|\bchartered\b|\baccountant|\baccounting\b|\baudit|\btax consultant|\bca firm\b)/i],
  ['legal', /(\blaw\b|\blegal\b|\badvocates?\b|\battorney|\bsolicitor|\blitigation\b|law chambers?|&\s*partners|\bllp\b)/i],
  ['healthcare', /(\bclinic|\bhospital|\bhealth ?care|\bdental|\bdentist|\bmedical\b|\bphysio|\bdiagnostic|\bpharmacy|\bwellness|\bnursing|smilecare|\bhealth\b)/i],
  ['realestate', /(\brealty\b|real ?estate|\brealtors?\b|\bproperties\b|\bproperty\b|\bestates?\b|\bbuilders?\b|\binfra\b|\bdevelopers?\b|\bhousing\b|\bhomes\b)/i],
  ['ecommerce', /(\becommerce|e-commerce|\bretail\b|\bd2c\b|\bkart\b|\bstore\b|\bshop\b|\bmart\b|\bbazaar\b|\bapparel|\bcosmetics)/i],
  ['manufacturing', /(\bmanufacturing|\bindustries\b|\bindustrial\b|\bfactory\b|engineering works|\bsteel\b|\btextiles?\b|\bplastics?\b|\bfabrication\b|\bmills?\b|\bfoundry\b)/i],
  ['edtech', /(\bedtech\b|\bacademy\b|\binstitute\b|\bcoaching\b|\bclasses\b|\beducation\b|\blearning\b|\bschool\b|\bcollege\b|\btutorials?\b|\bgurukul\b|\bvidyalaya\b)/i],
  ['nbfc', /(\bnbfc\b|\bfinance\b|\bfinancial\b|\bfintech\b|\bcapital\b|\blending\b|\bloans?\b|\bmicrofinance\b|\bfincorp\b)/i],
  ['recruitment', /(\brecruitment\b|\bstaffing\b|\bhr\b|human resource|\btalent\b|\bhiring\b|\bmanpower\b|\bplacements?\b)/i],
  ['architecture', /(\barchitects?\b|\barchitecture\b|\binterior|design studio|\bdesigns?\b)/i],
  ['logistics', /(\blogistics\b|\bfreight\b|\bcargo\b|\btransport|\bshipping\b|supply ?chain|\bmovers\b|\bpackers\b|\bcouriers?\b|\bwarehouse\b)/i],
  ['media', /(\bmedia\b|\bproductions?\b|\bfilms?\b|\bcreative\b|\badvertising\b|digital media|\bentertainment\b|pixelframe)/i],
  ['insurance', /(\binsurance\b|\binsur|\bassurance\b|\bbrokers?\b|\bbroking\b|securelife)/i],
  ['marketing', /(\bmarketing\b|\bagency\b|\bgrowth\b|\bbranding\b|\bseo\b|\badverti|\bresearch\b)/i],
  ['sales', /(\bsales\b|business development|\bbpo\b|call cent(er|re)|\btelecalling\b)/i],
  ['software', /(\btech\b|technolog|\bsoftware\b|\bsystems?\b|\bsolutions?\b|\blabs?\b|\binfotech\b|it services|\bdigital\b|\bapps?\b|\bcloud\b|\banalytics\b)/i],
];

// Suggest an industry key from a company name. Returns null if nothing matches
// confidently (caller keeps the current selection).
export function detectIndustry(companyName) {
  const name = String(companyName || '').trim();
  if (name.length < 3) return null;
  for (const [key, rx] of INDUSTRY_NAME_RULES) {
    if (rx.test(name)) return key;
  }
  return null;
}

// ─── Seniority tiers (industry-standard levels) ──────────────────────────────
// Every role carries a tier so the generated org follows a realistic pyramid:
// few at the top, more at the bottom.
export const TIERS = {
  1: { label: 'Leadership', short: 'Lead' },   // Partner / Head / Director
  2: { label: 'Management', short: 'Mgmt' },    // Manager / Lead
  3: { label: 'Senior', short: 'Senior' },      // Senior IC
  4: { label: 'Team', short: 'Team' },          // Associate / Junior / Executive
};

// ─── Default org charts per industry ─────────────────────────────────────────
// Realistic departments + industry-standard roles arranged as a seniority
// pyramid: [name, role, tier]. Tier 1=leadership … 4=team. Auto-fills when a
// rep picks an industry; fully editable afterwards.
export const INDUSTRY_ORGCHARTS = {
  software: [
    { name: 'Engineering', people: [
      ['Arjun Mehta', 'Engineering Manager', 2],
      ['Priya Nair', 'Senior Software Engineer', 3], ['Rohan Das', 'Senior Backend Engineer', 3],
      ['Sneha Iyer', 'Software Engineer', 4], ['Karan Shah', 'Frontend Engineer', 4], ['Ritu Bansal', 'QA Engineer', 4],
    ] },
    { name: 'Product & Design', people: [
      ['Sara Kapoor', 'Product Manager', 2], ['Vivek Rao', 'Senior Product Designer', 3], ['Tanya Sethi', 'UX Designer', 4],
    ] },
    { name: 'DevOps & IT', people: [
      ['Aditya Menon', 'DevOps Lead', 2], ['Farhan Ali', 'DevOps Engineer', 4],
    ] },
    { name: 'Client Success', people: [
      ['Neha Sharma', 'Customer Success Manager', 2], ['Rahul Verma', 'Support Engineer', 4],
    ] },
  ],
  ca: [
    { name: 'Partners', people: [
      ['Rajesh Talaviya', 'Managing Partner', 1], ['Meera Joshi', 'Partner — Audit & Assurance', 1],
    ] },
    { name: 'Audit & Assurance', people: [
      ['Priyanshu Porwal', 'Audit Manager', 2],
      ['Dhanesh Patel', 'Senior Audit Associate', 3], ['Komal Shah', 'Article Assistant', 4], ['Nikita Rao', 'Article Assistant', 4],
    ] },
    { name: 'Tax & Compliance', people: [
      ['Ankit Desai', 'Tax Manager', 2], ['Riya Mehta', 'Senior Tax Associate', 3], ['Yatin Shah', 'Tax Associate', 4],
    ] },
    { name: 'Bookkeeping', people: [
      ['Suresh Nair', 'Accounts Lead', 2], ['Pooja Rana', 'Accounts Executive', 4],
    ] },
  ],
  marketing: [
    { name: 'Marketing Leadership', people: [
      ['Ishaan Kapoor', 'Marketing Head', 1],
    ] },
    { name: 'Content & Research', people: [
      ['Devika Menon', 'Content Lead', 2], ['Ananya Ghosh', 'Senior Market Analyst', 3],
      ['Aryan Singh', 'Copywriter', 4], ['Tara Bose', 'Content Writer', 4],
    ] },
    { name: 'Performance Marketing', people: [
      ['Nikhil Jain', 'Performance Marketing Manager', 2], ['Sanya Kaur', 'Paid Ads Specialist', 4],
    ] },
    { name: 'Design & Creative', people: [
      ['Rohan Pillai', 'Creative Lead', 2], ['Maya Reddy', 'Graphic Designer', 4],
    ] },
  ],
  sales: [
    { name: 'Sales Leadership', people: [
      ['Vikram Malhotra', 'VP of Sales', 1],
    ] },
    { name: 'Account Executives', people: [
      ['Aarav Gupta', 'Sales Manager', 2], ['Kabir Anand', 'Senior Account Executive', 3],
      ['Diya Sharma', 'Account Executive', 4], ['Rohit Jain', 'Account Executive', 4],
    ] },
    { name: 'Sales Development', people: [
      ['Rhea Kapoor', 'SDR Team Lead', 2], ['Yash Patel', 'Sales Development Rep', 4], ['Isha Nair', 'Sales Development Rep', 4],
    ] },
    { name: 'Revenue Operations', people: [
      ['Manav Shah', 'RevOps Analyst', 3],
    ] },
  ],
  operations: [
    { name: 'Operations Leadership', people: [
      ['Suresh Kumar', 'Head of Operations', 1],
    ] },
    { name: 'Operations', people: [
      ['Lakshmi Menon', 'Operations Manager', 2], ['Amit Roy', 'Senior Ops Executive', 3],
      ['Sana Khan', 'Ops Executive', 4], ['Deepak Rao', 'Ops Executive', 4],
    ] },
    { name: 'Customer Support', people: [
      ['Fatima Sheikh', 'Support Team Lead', 2], ['Gaurav Bhat', 'Support Agent', 4], ['Nisha Pillai', 'Support Agent', 4],
    ] },
    { name: 'Quality & Reporting', people: [
      ['Rohit Saxena', 'Quality Lead', 2], ['Divya Rao', 'Quality Analyst', 4], ['Harish Nair', 'Data Analyst', 3],
    ] },
  ],
  legal: [
    { name: 'Partners', people: [
      ['Rohan Khanna', 'Managing Partner', 1], ['Aditi Verma', 'Partner — Litigation', 1],
    ] },
    { name: 'Litigation', people: [
      ['Sameer Joshi', 'Senior Associate', 3], ['Neha Iyer', 'Associate', 4], ['Kunal Shah', 'Junior Associate', 4],
    ] },
    { name: 'Corporate & Advisory', people: [
      ['Priya Menon', 'Senior Associate', 3], ['Arjun Rao', 'Associate', 4],
    ] },
    { name: 'Paralegal & Support', people: [
      ['Meera Nair', 'Paralegal Lead', 2], ['Ravi Kumar', 'Legal Assistant', 4],
    ] },
  ],
  healthcare: [
    { name: 'Doctors', people: [
      ['Dr. Anil Kapoor', 'Chief Physician', 1], ['Dr. Sneha Rao', 'Consultant', 3], ['Dr. Vikram Shah', 'Consultant', 3],
    ] },
    { name: 'Nursing & Clinical', people: [
      ['Sister Mary Thomas', 'Head Nurse', 2], ['Pooja Nair', 'Staff Nurse', 4], ['Reena Das', 'Staff Nurse', 4],
    ] },
    { name: 'Front Desk & Billing', people: [
      ['Kavya Iyer', 'Front-Office Lead', 2], ['Amit Sharma', 'Billing Executive', 4],
    ] },
    { name: 'Diagnostics', people: [
      ['Suresh Menon', 'Lab Technician', 4],
    ] },
  ],
  realestate: [
    { name: 'Leadership', people: [
      ['Rajesh Agarwal', 'Principal Broker', 1],
    ] },
    { name: 'Sales', people: [
      ['Vikram Malhotra', 'Sales Manager', 2], ['Kabir Anand', 'Senior Sales Executive', 3],
      ['Diya Sharma', 'Sales Executive', 4], ['Rohit Jain', 'Sales Executive', 4],
    ] },
    { name: 'Lead Generation', people: [
      ['Rhea Kapoor', 'Telecalling Lead', 2], ['Yash Patel', 'Telecaller', 4], ['Isha Nair', 'Telecaller', 4],
    ] },
    { name: 'Documentation', people: [
      ['Manav Shah', 'Documentation Executive', 4],
    ] },
  ],
  ecommerce: [
    { name: 'Leadership', people: [
      ['Ananya Gupta', 'Founder / Head', 1],
    ] },
    { name: 'Marketing & Growth', people: [
      ['Nikhil Jain', 'Performance Marketing Manager', 2], ['Sanya Kaur', 'Paid Ads Specialist', 4], ['Aryan Singh', 'Content Executive', 4],
    ] },
    { name: 'Catalogue & Ops', people: [
      ['Priya Nair', 'Catalogue Lead', 2], ['Rohan Das', 'Operations Executive', 4],
    ] },
    { name: 'Customer Support', people: [
      ['Neha Sharma', 'Support Lead', 2], ['Karan Shah', 'Support Executive', 4],
    ] },
  ],
  manufacturing: [
    { name: 'Plant Leadership', people: [
      ['Suresh Kumar', 'Plant Head', 1],
    ] },
    { name: 'Production', people: [
      ['Lakshmi Menon', 'Production Manager', 2], ['Amit Roy', 'Shift Supervisor', 3],
      ['Deepak Rao', 'Machine Operator', 4], ['Sana Khan', 'Machine Operator', 4],
    ] },
    { name: 'Procurement & Stores', people: [
      ['Rohit Saxena', 'Purchase Manager', 2], ['Divya Rao', 'Stores Executive', 4],
    ] },
    { name: 'Quality & Dispatch', people: [
      ['Harish Nair', 'QC Lead', 2], ['Farhan Ali', 'Dispatch Executive', 4],
    ] },
  ],
  edtech: [
    { name: 'Academics', people: [
      ['Dr. Meera Joshi', 'Academic Head', 1], ['Ishaan Kapoor', 'Senior Faculty', 3], ['Tara Bose', 'Faculty', 4],
    ] },
    { name: 'Content', people: [
      ['Devika Menon', 'Content Lead', 2], ['Aryan Singh', 'Content Developer', 4],
    ] },
    { name: 'Admissions & Counselling', people: [
      ['Nikhil Jain', 'Counselling Manager', 2], ['Sanya Kaur', 'Admissions Counsellor', 4], ['Yash Patel', 'Admissions Counsellor', 4],
    ] },
    { name: 'Student Support', people: [
      ['Rhea Kapoor', 'Support Executive', 4],
    ] },
  ],
  nbfc: [
    { name: 'Leadership', people: [
      ['Vikram Malhotra', 'Business Head', 1],
    ] },
    { name: 'Credit & Underwriting', people: [
      ['Aarav Gupta', 'Credit Manager', 2], ['Kabir Anand', 'Senior Credit Analyst', 3], ['Diya Sharma', 'Credit Analyst', 4],
    ] },
    { name: 'Collections', people: [
      ['Rhea Kapoor', 'Collections Lead', 2], ['Yash Patel', 'Recovery Executive', 4], ['Isha Nair', 'Recovery Executive', 4],
    ] },
    { name: 'Operations & Compliance', people: [
      ['Manav Shah', 'Compliance Officer', 3], ['Pooja Rana', 'Ops Executive', 4],
    ] },
  ],
  recruitment: [
    { name: 'Leadership', people: [
      ['Ishaan Kapoor', 'Delivery Head', 1],
    ] },
    { name: 'Recruitment', people: [
      ['Devika Menon', 'Team Lead — Recruiting', 2], ['Ananya Ghosh', 'Senior Recruiter', 3],
      ['Aryan Singh', 'Recruiter', 4], ['Tara Bose', 'Recruiter', 4],
    ] },
    { name: 'Sourcing', people: [
      ['Nikhil Jain', 'Sourcing Lead', 2], ['Sanya Kaur', 'Sourcing Associate', 4],
    ] },
    { name: 'Client & Account', people: [
      ['Rohan Pillai', 'Account Manager', 2], ['Maya Reddy', 'Coordinator', 4],
    ] },
  ],
  architecture: [
    { name: 'Principals', people: [
      ['Rohan Khanna', 'Principal Architect', 1],
    ] },
    { name: 'Design', people: [
      ['Aditi Verma', 'Senior Architect', 3], ['Sameer Joshi', 'Architect', 4], ['Neha Iyer', 'Junior Architect', 4],
    ] },
    { name: 'Interiors & Visualisation', people: [
      ['Priya Menon', 'Interior Design Lead', 2], ['Arjun Rao', '3D Visualiser', 4],
    ] },
    { name: 'Site & Estimation', people: [
      ['Meera Nair', 'Site Coordinator', 3], ['Ravi Kumar', 'Estimation Executive', 4],
    ] },
  ],
  logistics: [
    { name: 'Leadership', people: [
      ['Suresh Kumar', 'Operations Head', 1],
    ] },
    { name: 'Fleet & Dispatch', people: [
      ['Lakshmi Menon', 'Dispatch Manager', 2], ['Amit Roy', 'Route Planner', 3],
      ['Deepak Rao', 'Dispatch Executive', 4], ['Sana Khan', 'Dispatch Executive', 4],
    ] },
    { name: 'Documentation & Customs', people: [
      ['Rohit Saxena', 'Documentation Lead', 2], ['Divya Rao', 'Documentation Executive', 4],
    ] },
    { name: 'Client Servicing', people: [
      ['Harish Nair', 'Account Manager', 3], ['Farhan Ali', 'Coordinator', 4],
    ] },
  ],
  media: [
    { name: 'Leadership', people: [
      ['Ananya Gupta', 'Creative Director', 1],
    ] },
    { name: 'Production', people: [
      ['Nikhil Jain', 'Production Manager', 2], ['Sanya Kaur', 'Videographer', 4], ['Aryan Singh', 'Videographer', 4],
    ] },
    { name: 'Post-Production', people: [
      ['Rohan Pillai', 'Lead Editor', 2], ['Maya Reddy', 'Video Editor', 4], ['Tara Bose', 'Motion Designer', 4],
    ] },
    { name: 'Content & Accounts', people: [
      ['Devika Menon', 'Content Lead', 3], ['Yash Patel', 'Account Executive', 4],
    ] },
  ],
  insurance: [
    { name: 'Leadership', people: [
      ['Vikram Malhotra', 'Principal Advisor', 1],
    ] },
    { name: 'Sales & Advisory', people: [
      ['Aarav Gupta', 'Sales Manager', 2], ['Kabir Anand', 'Senior Advisor', 3],
      ['Diya Sharma', 'Insurance Advisor', 4], ['Rohit Jain', 'Insurance Advisor', 4],
    ] },
    { name: 'Claims & Servicing', people: [
      ['Rhea Kapoor', 'Claims Lead', 2], ['Yash Patel', 'Claims Executive', 4],
    ] },
    { name: 'Operations', people: [
      ['Manav Shah', 'Operations Executive', 4],
    ] },
  ],
};

// Build a fresh, editable hierarchy (with unique ids) for an industry.
// idFn keeps id generation in the UI layer's control. Preserves tier on each person.
export function orgChartFor(industryKey, idFn) {
  const chart = INDUSTRY_ORGCHARTS[industryKey] || INDUSTRY_ORGCHARTS.software;
  return chart.map((d) => ({
    id: idFn(),
    name: d.name,
    people: d.people.map(([name, role, tier]) => ({ id: idFn(), name, role, tier: tier || 4 })),
  }));
}

// Infer a seniority tier from a role title (for hand-typed/pasted people).
export function tierFromRole(role) {
  const r = String(role || '').toLowerCase();
  if (/\b(partner|founder|ceo|cto|coo|cfo|vp|vice president|head|director|principal)\b/.test(r)) return 1;
  if (/\b(manager|lead|head of)\b/.test(r)) return 2;
  if (/\bsenior|sr\.?\b/.test(r)) return 3;
  return 4;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const round = (n) => Math.round(n);
const between = (rng, lo, hi) => lo + rng() * (hi - lo);
export const initials = (name) =>
  String(name || '?').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
export const titleCase = (s) =>
  String(s || '').replace(/\w\S*/g, (t) => t[0].toUpperCase() + t.slice(1).toLowerCase());
export const INR = (n) =>
  '₹' + (n >= 10000000 ? (n / 10000000).toFixed(2) + ' Cr' : n >= 100000 ? (n / 100000).toFixed(1) + ' L' : Math.round(n).toLocaleString('en-IN'));

// ─── Core generator ──────────────────────────────────────────────────────────
/**
 * @param {Object} cfg
 * @param {string} cfg.company        Prospect company name
 * @param {string} cfg.industryKey    Key into INDUSTRIES
 * @param {string} cfg.period         Human label e.g. "June 2026"
 * @param {number} cfg.workingDays    Working days in the window (default 22)
 * @param {Array}  cfg.departments    [{ name, people: [{name, role}] }]
 * @returns rich model consumed by reportTemplates.js
 */
export function generateReport(cfg) {
  const company = (cfg.company || 'Sample Company').trim();
  const industry = INDUSTRIES[cfg.industryKey] || INDUSTRIES.software;
  const lingo = lingoFor(INDUSTRIES[cfg.industryKey] ? cfg.industryKey : 'software');
  const plural = (w) => (/s$|team|practice|firm|studio|clinic|plant|academy$/i.test(w) ? w : w + 's');
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s); // sentence-case only
  // First name, skipping honorifics (Dr./Mr./Ms./CA etc.) so we never print "Dr." alone.
  const firstName = (full) => {
    const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
    const skip = /^(dr|mr|mrs|ms|miss|prof|ca|adv|sri|smt)\.?$/i;
    const w = parts.find((p) => !skip.test(p)) || parts[0] || 'A colleague';
    return titleCase(w);
  };
  const period = cfg.period || 'This Month';
  const workingDays = cfg.workingDays || 22;
  const rng = mulberry32(hashStr(company + '|' + cfg.industryKey + '|' + period));

  // Flatten hierarchy -> people, keeping their department
  const departments = (cfg.departments || []).filter((d) => d && (d.people || []).length);
  const people = [];
  departments.forEach((dept) => {
    (dept.people || []).forEach((p) => {
      if (!p || !p.name) return;
      const role = (p.role || '').trim();
      people.push({ name: p.name.trim(), role, dept: dept.name, tier: p.tier || tierFromRole(role) });
    });
  });
  if (!people.length) return null;

  // Per-person fabricated metrics (industry-anchored, seeded, internally consistent)
  people.forEach((p, idx) => {
    const r = mulberry32(hashStr(company + p.name + idx));
    // Hours by seniority: leadership tracks fewer hands-on hours (more oversight),
    // seniors & team log the most. Keeps the data consistent with the hierarchy.
    const tierHrs = { 1: [5.6, 7.8], 2: [6.2, 8.4], 3: [6.8, 9.0], 4: [6.9, 9.3] }[p.tier] || [6.8, 9.2];
    const dailyHrs = between(r, tierHrs[0], tierHrs[1]);
    const daysWorked = round(between(r, workingDays * 0.78, workingDays * 0.98));
    p.hours = round(dailyHrs * daysWorked);
    p.focus = round(between(r, 68, 88)); // % active/focus
    p.active = round((p.hours * p.focus) / 100);
    p.days = daysWorked;
    p.sessions = round(daysWorked * between(r, 2.4, 4.2));
    p.late = r() < 0.28 ? round(between(r, 2, 8)) : round(between(r, 0, 2));
    p.weekend = r() < 0.22 ? round(between(r, 2, 6)) : 0;

    // work mix — perturb the industry template per person
    const wm = industry.work.map(([k, w]) => [k, Math.max(1, w + between(r, -6, 6))]);
    const wsum = wm.reduce((a, [, w]) => a + w, 0);
    p.topWork = wm
      .map(([k, w]) => [k, round((w / wsum) * 100)])
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // tools — sample 3-6 from the industry set, deterministic
    const pool = industry.tools.slice();
    const nTools = 3 + Math.floor(r() * 4);
    const chosen = [];
    for (let i = 0; i < nTools && pool.length; i++) chosen.push(pool.splice(Math.floor(r() * pool.length), 1)[0]);
    p.topTools = chosen.map((t) => [t, round(between(r, 8, 60))]).sort((a, b) => b[1] - a[1]);
    p.usesAI = p.topTools.some(([t]) => industry.aiTools.includes(t)) || r() < 0.7;
  });

  people.sort((a, b) => b.hours - a.hours);

  // Firm-wide rollups
  const totalHours = round(people.reduce((s, p) => s + p.hours, 0));
  const totalActive = round(people.reduce((s, p) => s + p.active, 0));
  const focusPct = totalHours ? round((totalActive / totalHours) * 100) : 0;
  const aiUsers = people.filter((p) => p.usesAI).length;
  const top2 = people.slice(0, 2);
  const top2Share = totalHours ? round(((top2[0]?.hours || 0) + (top2[1]?.hours || 0)) / totalHours * 100) : 0;

  // Firm work mix (weighted by hours)
  const workAgg = {};
  people.forEach((p) => p.topWork.forEach(([k, v]) => { workAgg[k] = (workAgg[k] || 0) + v * p.hours; }));
  const workSum = Object.values(workAgg).reduce((a, b) => a + b, 0) || 1;
  const workMix = Object.entries(workAgg)
    .map(([k, v]) => [k, round((v / workSum) * 100)])
    .sort((a, b) => b[1] - a[1]);

  // Tool adoption (# of people using each)
  const toolUsers = {};
  people.forEach((p) => p.topTools.forEach(([t]) => { toolUsers[t] = (toolUsers[t] || 0) + 1; }));
  const toolMix = Object.entries(toolUsers).map(([t, n]) => [t, n]).sort((a, b) => b[1] - a[1]).slice(0, 8);

  // Daily activity trend across the window
  const dayTrend = [];
  const baseDaily = totalHours / workingDays;
  for (let i = 0; i < Math.min(workingDays, 22); i++) {
    const dr = mulberry32(hashStr(company + 'day' + i));
    const dow = i % 7;
    const isWeekend = dow === 5 || dow === 6;
    const h = round(baseDaily * (isWeekend ? between(dr, 0.15, 0.4) : between(dr, 0.82, 1.15)));
    dayTrend.push({ i, h, wd: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][dow], we: isWeekend });
  }

  // Department rollups
  const deptRollup = departments.map((d) => {
    const dp = people.filter((p) => p.dept === d.name);
    const h = round(dp.reduce((s, p) => s + p.hours, 0));
    const f = h ? round(dp.reduce((s, p) => s + p.active, 0) / h * 100) : 0;
    const top = dp.slice().sort((a, b) => b.hours - a.hours)[0];
    const share = totalHours ? round(h / totalHours * 100) : 0;
    let status = 'Healthy', col = '#1f9d6b';
    if (f < 70) { status = 'Watch'; col = '#e8912d'; }
    if (dp.length === 1) { status = 'Single-point'; col = '#e8912d'; }
    return { name: d.name, count: dp.length, hours: h, focus: f, share, top, status, col };
  }).sort((a, b) => b.hours - a.hours);

  // Automation opportunities — scale ₹ by headcount
  const automations = industry.automations.map(([task, pct, perHead]) => ({
    task, pct, save: round(perHead * Math.max(1, people.length) * between(rng, 0.35, 0.6)),
    today: 'Currently handled manually and repetitively across the team each cycle.',
    replace: 'Templated workflow + AI-assisted execution frees senior time for review.',
  }));
  const totalRecoverable = automations.reduce((s, a) => s + a.save, 0);

  // "Three things that matter" — insight blocks, each ending in a decision
  const topP = people[0];
  const signals = [
    {
      tag: 'Concentration', col: '#e8912d',
      title: `Delivery is concentrated in your top ${top2.length} ${plural(lingo.unit)}`,
      desc: `${top2.map((p) => firstName(p.name)).join(' & ')} together account for roughly ${top2Share}% of the ${period} output. If either is unavailable at ${lingo.deadline}, throughput drops sharply.`,
      decision: `${cap(lingo.crossTrain)} so no single ${lingo.unit} is a bottleneck.`,
    },
    {
      tag: 'AI adoption', col: '#0d6e6e',
      title: `AI is already part of the daily workflow`,
      desc: `${aiUsers} of ${people.length} ${plural(lingo.unit)} show consistent AI-assisted activity — most visibly in ${lingo.routine}. Adoption is organic, so the gains are real but uneven.`,
      decision: `Standardise the winning AI workflows so the whole ${lingo.team} captures the same gains.`,
    },
    {
      tag: 'Focus', col: focusPct >= 78 ? '#1f9d6b' : '#e8912d',
      title: `The ${lingo.team} runs at ${focusPct}% focus`,
      desc: `Across ${totalHours.toLocaleString('en-IN')} tracked hours, ${focusPct}% was active, on-task time — ${focusPct >= 78 ? 'a strong, disciplined signal' : 'with room to reclaim through fewer interruptions'}.`,
      decision: focusPct >= 78 ? `Protect this: shield ${plural(lingo.unit)} from avoidable interruptions during peak hours.` : `Reduce fragmentation — batch admin work and protect time for ${lingo.seniorValue}.`,
    },
  ];

  // Risks — each with a move
  const risks = [
    {
      sev: 'Medium', col: '#e8912d', t: `Output concentrated in a few ${plural(lingo.unit)}`,
      signal: `top two ≈ ${top2Share}%`,
      d: `A small number of ${plural(lingo.unit)} carry a disproportionate share of delivery. This is efficient today but fragile at ${lingo.deadline} or during leave.`,
      move: `${cap(lingo.crossTrain)} so coverage survives any one absence.`,
    },
    {
      sev: 'Upside', col: '#0d6e6e', t: `Clear automation upside in ${lingo.routine}`,
      signal: `~${INR(totalRecoverable)}/yr recoverable (est.)`,
      d: `${cap(lingo.routine)} is high-volume and repetitive — a strong candidate for automation, especially with AI already in the ${lingo.team}'s workflow.`,
      move: `Adopt templated + AI-assisted execution for ${lingo.routine}; free senior time for ${lingo.seniorValue}.`,
    },
  ];
  const burnoutPeople = people.filter((p) => p.late >= 5 || p.weekend >= 4);
  if (burnoutPeople.length) {
    risks.push({
      sev: 'Watch', col: '#d64550', t: `Early burnout signals in a few ${plural(lingo.unit)}`,
      signal: `${burnoutPeople.length} showing late / weekend load`,
      d: `${burnoutPeople.slice(0, 3).map((p) => firstName(p.name)).join(', ')}${burnoutPeople.length > 3 ? ' and others' : ''} show recurring late-hours or weekend working — sustainable short-term, a retention risk if prolonged.`,
      move: `Rebalance load off the top ${plural(lingo.unit)} and check in before it compounds.`,
    });
  }

  // Action items — ranked, quantified
  const actions = [
    { t: `Cross-train the top ${plural(lingo.unit)}`, d: `Remove single-${lingo.unit} dependency on ${topP ? firstName(topP.name) : 'your lead'}'s work before ${lingo.deadline}.`, imp: 'Resilience' },
    { t: `Automate ${lingo.routine}`, d: `Start with "${automations[0].task}" — highest volume, most repetitive.`, imp: INR(automations[0].save) + '/yr' },
    { t: 'Standardise the AI workflows', d: `Codify the AI workflows already used by ${aiUsers} ${plural(lingo.unit)} so the whole ${lingo.team} captures the gain.`, imp: 'Leverage' },
    { t: `Protect time for ${lingo.seniorValue}`, d: `Shield senior ${plural(lingo.unit)} from ${lingo.routine} so they focus on ${lingo.seniorValue}.`, imp: 'Focus' },
  ];

  // Overall verdict
  let verdict = { label: 'Healthy', col: '#1f9d6b' };
  if (focusPct < 70 || burnoutPeople.length > people.length * 0.3) verdict = { label: 'Watch', col: '#e8912d' };
  verdict.text = `A ${focusPct >= 78 ? 'high-output, disciplined' : 'productive but improvable'} ${industry.verdictNoun} — ${workMix[0][0].toLowerCase()} and ${(workMix[1] || workMix[0])[0].toLowerCase()} carried the ${lingo.team}, delivered at ${focusPct}% focus with strong attendance.`;

  return {
    company, period, workingDays, industry: industry.label, lingo,
    people, departments: deptRollup,
    kpis: {
      people: people.length, hours: totalHours, focus: focusPct,
      aiUsers, days: workingDays, sessions: people.reduce((s, p) => s + p.sessions, 0),
    },
    workMix, toolMix, dayTrend, signals, risks, actions, automations, totalRecoverable,
    top2Share, aiUsers, verdict,
  };
}
